/**
 * T022 — 全局工作量查询（模块十五 4.12 / R-31）
 *
 * 场景 15.1: 所有角色（member/manager/admin）均有入口，查全团队不受 scope 限制。
 * 场景 15.2: 多维筛选 + 日期维度 toggle（按 start_date / 按 complete_date）。
 * 场景 15.3: 结果表以 work_logs 为主左连 personal_task，纯任务独立行，ad-hoc 两日期列空。
 * 场景 15.4: 默认每页 50；>5000 行提示收窄并显示前 5000。
 * 场景 15.5: 导出 CS_Workload_Search_YYYY-MM-DD.csv（BOM），含 id/created_at/approved_by/start_date/complete_date，不含 work_date。
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import { useLang } from '../i18n'

// ── Types ───────────────────────────────────────────────────────────────────

interface SearchRow {
  id: string
  row_type: 'work_log' | 'personal_task'
  work_log_id: string | null
  member_id: string
  member_display_name: string
  is_group_lead: boolean
  account_status: string
  region_code_snapshot: string
  group_name_snapshot: string
  category_code: string
  category_display_name_en: string
  category_display_name_cn: string
  task_id: string | null
  task_name: string
  counting_rule: string | null
  volume: number | null
  hours: string
  note: string | null
  is_compliant: boolean | null
  start_date: string | null
  complete_date: string | null
  task_status: string | null
  created_at: string | null
  approved_by: string | null
}

interface SearchTotals {
  task_count: number
  volume_total: number
  hours_total: number
}

interface SearchResponse {
  data: SearchRow[]
  page: { page: number; page_size: number; total: number; real_total: number }
  truncated: boolean
  date_dim: string
  totals: SearchTotals
}

interface MemberItem { id: string; display_name: string; role: string }
interface CategoryItem { code: string; display_name_en: string; display_name_cn: string }
interface TaskItem { id: string; name: string; category_code: string }

// ── Helpers ─────────────────────────────────────────────────────────────────

function buildSearchUrl(params: {
  member: string; task: string; category: string; region: string
  compliant: string; taskStatus: string; keyword: string
  dateFrom: string; dateTo: string; dateDim: string; page: number
}): string {
  const p = new URLSearchParams({ page: String(params.page), page_size: '50', date_dim: params.dateDim })
  if (params.member) p.set('member', params.member)
  if (params.task) p.set('task', params.task)
  if (params.category) p.set('category', params.category)
  if (params.region) p.set('region', params.region)
  if (params.compliant) p.set('compliant', params.compliant)
  if (params.taskStatus) p.set('task_status', params.taskStatus)
  if (params.keyword.trim()) p.set('keyword', params.keyword.trim())
  if (params.dateFrom) p.set('date_from', params.dateFrom)
  if (params.dateTo) p.set('date_to', params.dateTo)
  return `/api/global-search?${p}`
}

function buildExportUrl(params: {
  member: string; task: string; category: string; region: string
  compliant: string; taskStatus: string; keyword: string
  dateFrom: string; dateTo: string; dateDim: string
}): string {
  const p = new URLSearchParams({ date_dim: params.dateDim })
  if (params.member) p.set('member', params.member)
  if (params.task) p.set('task', params.task)
  if (params.category) p.set('category', params.category)
  if (params.region) p.set('region', params.region)
  if (params.compliant) p.set('compliant', params.compliant)
  if (params.taskStatus) p.set('task_status', params.taskStatus)
  if (params.keyword.trim()) p.set('keyword', params.keyword.trim())
  if (params.dateFrom) p.set('date_from', params.dateFrom)
  if (params.dateTo) p.set('date_to', params.dateTo)
  return `/api/global-search/export.csv?${p}`
}

// ── Styles ──────────────────────────────────────────────────────────────────

const cellStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--border)',
  fontSize: 13,
  verticalAlign: 'top',
  whiteSpace: 'nowrap',
}

const thStyle: React.CSSProperties = {
  ...cellStyle,
  background: 'var(--bg-page)',
  fontWeight: 600,
  borderBottom: '2px solid var(--border)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
}

const inputStyle: React.CSSProperties = {
  padding: '4px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontSize: 13,
  background: 'var(--bg-surface)',
  height: 32,
  boxSizing: 'border-box',
}

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
  minWidth: 110,
}

// ── Main Component ───────────────────────────────────────────────────────────

export function GlobalSearchPage() {
  const { t } = useLang()
  const navigate = useNavigate()
  const { accessToken, user } = useAuth()
  const canEdit = user?.role === 'manager' || user?.role === 'admin'

  // Filter state
  const [member, setMember] = useState('')
  const [taskId, setTaskId] = useState('')
  const [category, setCategory] = useState('')
  const [region, setRegion] = useState('')
  const [compliant, setCompliant] = useState('')
  const [taskStatus, setTaskStatus] = useState('')
  const [keyword, setKeyword] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [dateDim, setDateDim] = useState<'start_date' | 'complete_date'>('start_date')

  // Pagination
  const [page, setPage] = useState(1)

  // Data state
  const [result, setResult] = useState<SearchResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [exporting, setExporting] = useState(false)

  // Reference data for dropdowns
  const [members, setMembers] = useState<MemberItem[]>([])
  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [tasks, setTasks] = useState<TaskItem[]>([])

  // Load reference data
  useEffect(() => {
    if (!accessToken) return
    const h = { Authorization: `Bearer ${accessToken}` }
    fetch('/api/members?page_size=200', { headers: h })
      .then(r => r.json())
      .then(d => setMembers((d.members ?? []).filter((m: MemberItem) => m.role !== 'admin')))
      .catch(() => { })

    fetch('/api/categories', { headers: h })
      .then(r => r.json())
      .then(d => setCategories(d.data ?? []))
      .catch(() => { })

    fetch('/api/tasks?include_inactive=true', { headers: h })
      .then(r => r.json())
      .then(d => setTasks(d.data ?? []))
      .catch(() => { })
  }, [accessToken])

  const doSearch = useCallback(async (pg: number) => {
    if (!accessToken) return
    setLoading(true)
    setError('')
    try {
      const url = buildSearchUrl({ member, task: taskId, category, region, compliant, taskStatus, keyword, dateFrom, dateTo, dateDim, page: pg })
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      const json = await resp.json()
      if (!resp.ok) {
        setError(json.error?.message ?? t('gsSearchError'))
        setResult(null)
      } else {
        setResult(json)
      }
    } catch {
      setError(t('gsSearchError'))
    } finally {
      setLoading(false)
    }
  }, [member, taskId, category, region, compliant, taskStatus, keyword, dateFrom, dateTo, dateDim, t, accessToken])

  useEffect(() => {
    doSearch(page)
  }, [doSearch, page])

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [member, taskId, category, region, compliant, taskStatus, keyword, dateFrom, dateTo, dateDim])

  const handleExport = async () => {
    if (!accessToken) return
    setExporting(true)
    try {
      const url = buildExportUrl({ member, task: taskId, category, region, compliant, taskStatus, keyword, dateFrom, dateTo, dateDim })
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      if (!resp.ok) {
        const j = await resp.json()
        alert(j.error?.message ?? t('gsExportError'))
        return
      }
      const blob = await resp.blob()
      const cd = resp.headers.get('content-disposition') ?? ''
      const match = cd.match(/filename="([^"]+)"/)
      const filename = match ? match[1] : `CS_Workload_Search_${new Date().toISOString().slice(0, 10)}.csv`
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = filename
      a.click()
      URL.revokeObjectURL(a.href)
    } catch {
      alert(t('gsExportError'))
    } finally {
      setExporting(false)
    }
  }

  const clearFilter = (key: string) => {
    if (key === 'member') setMember('')
    else if (key === 'task') setTaskId('')
    else if (key === 'category') setCategory('')
    else if (key === 'region') setRegion('')
    else if (key === 'compliant') setCompliant('')
    else if (key === 'taskStatus') setTaskStatus('')
    else if (key === 'keyword') setKeyword('')
    else if (key === 'date') { setDateFrom(''); setDateTo('') }
  }

  const totalPages = result ? Math.ceil(result.page.total / result.page.page_size) : 1

  // Active chips
  const chips: Array<{ key: string; label: string }> = []
  if (member) {
    const m = members.find(x => x.id === member)
    chips.push({ key: 'member', label: `${t('gsMember')}: ${m?.display_name ?? member}` })
  }
  if (taskId) {
    const tk = tasks.find(x => x.id === taskId)
    chips.push({ key: 'task', label: `${t('gsTask')}: ${tk?.name ?? taskId}` })
  }
  if (category) {
    const cat = categories.find(x => x.code === category)
    chips.push({ key: 'category', label: `${t('gsCategory')}: ${cat?.display_name_en ?? category}` })
  }
  if (region) chips.push({ key: 'region', label: `${t('gsRegion')}: ${region}` })
  if (compliant) chips.push({ key: 'compliant', label: `${t('gsCompliant')}: ${compliant === 'true' ? t('gsCompliantYes') : t('gsCompliantNo')}` })
  if (taskStatus) chips.push({ key: 'taskStatus', label: `${t('gsTaskStatus')}: ${taskStatus}` })
  if (keyword) chips.push({ key: 'keyword', label: `${t('gsKeyword')}: ${keyword}` })
  if (dateFrom || dateTo) chips.push({ key: 'date', label: `${dateFrom ?? '…'} → ${dateTo ?? '…'} (${dateDim === 'start_date' ? t('gsDateDimStart') : t('gsDateDimComplete')})` })

  const tasksForCategory = (code: string) => tasks.filter(tk => tk.category_code === code)

  return (
    <div style={{ padding: '20px 24px', fontFamily: 'system-ui, sans-serif', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{t('gsTitle')}</h2>
        {canEdit && (
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            style={{
              padding: '6px 18px',
              background: 'var(--success)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: exporting ? 'not-allowed' : 'pointer',
              fontSize: 13,
            }}
          >
            {exporting ? t('loading') : t('gsExport')}
          </button>
        )}
        {!canEdit && (
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            style={{
              padding: '6px 18px',
              background: 'var(--success)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: exporting ? 'not-allowed' : 'pointer',
              fontSize: 13,
            }}
          >
            {exporting ? t('loading') : t('gsExport')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, padding: '12px 16px', marginBottom: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>

          {/* Member */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsMember')}</label>
            <select value={member} onChange={e => setMember(e.target.value)} style={selectStyle}>
              <option value="">{t('gsAll')}</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>{m.display_name}</option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsCategory')}</label>
            <select value={category} onChange={e => { setCategory(e.target.value); setTaskId('') }} style={selectStyle}>
              <option value="">{t('gsAll')}</option>
              {categories.map(c => (
                <option key={c.code} value={c.code}>{c.display_name_en}</option>
              ))}
            </select>
          </div>

          {/* Task */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsTask')}</label>
            <select value={taskId} onChange={e => setTaskId(e.target.value)} style={{ ...selectStyle, minWidth: 140 }}>
              <option value="">{t('gsAll')}</option>
              {(category ? tasksForCategory(category) : tasks).map(tk => (
                <option key={tk.id} value={tk.id}>{tk.name}</option>
              ))}
            </select>
          </div>

          {/* Region */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsRegion')}</label>
            <select value={region} onChange={e => setRegion(e.target.value)} style={selectStyle}>
              <option value="">{t('gsAll')}</option>
              <option value="CN">CN</option>
              <option value="US">US</option>
            </select>
          </div>

          {/* Compliant */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsCompliant')}</label>
            <select value={compliant} onChange={e => setCompliant(e.target.value)} style={selectStyle}>
              <option value="">{t('gsAll')}</option>
              <option value="true">{t('gsCompliantYes')}</option>
              <option value="false">{t('gsCompliantNo')}</option>
            </select>
          </div>

          {/* Task Status */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsTaskStatus')}</label>
            <select value={taskStatus} onChange={e => setTaskStatus(e.target.value)} style={selectStyle}>
              <option value="">{t('gsAll')}</option>
              <option value="todo">{t('assignStatusTodo')}</option>
              <option value="progress">{t('assignStatusProgress')}</option>
              <option value="done">{t('assignStatusDone')}</option>
            </select>
          </div>

          {/* Keyword */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsKeyword')}</label>
            <input
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder={t('gsKeywordPlaceholder')}
              style={{ ...inputStyle, width: 140 }}
            />
          </div>

          {/* Date range */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsDateFrom')}</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...inputStyle, width: 130 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsDateTo')}</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ ...inputStyle, width: 130 }} />
          </div>

          {/* Date dimension toggle */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('gsDateDim')}</label>
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', height: 32 }}>
              <button
                onClick={() => setDateDim('start_date')}
                style={{
                  padding: '0 10px',
                  border: 'none',
                  background: dateDim === 'start_date' ? 'var(--accent)' : 'var(--bg-surface)',
                  color: dateDim === 'start_date' ? 'var(--accent-contrast)' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: dateDim === 'start_date' ? 600 : 400,
                }}
              >
                {t('gsDateDimStart')}
              </button>
              <button
                onClick={() => setDateDim('complete_date')}
                style={{
                  padding: '0 10px',
                  border: 'none',
                  borderLeft: '1px solid var(--border)',
                  background: dateDim === 'complete_date' ? 'var(--accent)' : 'var(--bg-surface)',
                  color: dateDim === 'complete_date' ? 'var(--accent-contrast)' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: dateDim === 'complete_date' ? 600 : 400,
                }}
              >
                {t('gsDateDimComplete')}
              </button>
            </div>
          </div>
        </div>

        {/* Active filter chips */}
        {chips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {chips.map(chip => (
              <span
                key={chip.key}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'var(--info-bg)', color: 'var(--accent)', borderRadius: 4,
                  padding: '2px 8px', fontSize: 12,
                }}
              >
                {chip.label}
                <button
                  onClick={() => clearFilter(chip.key)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 14, lineHeight: 1, padding: 0 }}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* >5000 truncation warning */}
      {result?.truncated && (
        <div style={{
          background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 4,
          padding: '8px 14px', marginBottom: 10, fontSize: 13, color: 'var(--warning)',
        }}>
          ⚠ {t('gsTruncatedWarning').replace('{total}', String(result.page.real_total))}
        </div>
      )}

      {/* Stats row */}
      {result && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
          {t('gsTotal')}: {result.page.real_total} &nbsp;|&nbsp; {t('gsDisplaying')}: {result.page.total}
        </div>
      )}

      {/* Error */}
      {error && <div style={{ color: 'var(--danger)', marginBottom: 8, fontSize: 13 }}>{error}</div>}

      {/* Table */}
      <div style={{ overflowX: 'auto', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>{t('loading')}</div>
        ) : result && result.data.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>{t('gsNoResults')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={thStyle}>{t('gsStartDate')}</th>
                <th style={thStyle}>{t('gsCompleteDate')}</th>
                <th style={thStyle}>{t('gsMemberCol')}</th>
                <th style={thStyle}>{t('gsRegionGroup')}</th>
                <th style={thStyle}>{t('gsCategory')}</th>
                <th style={thStyle}>{t('gsTaskCol')}</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>{t('gsVolume')}</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>{t('gsHours')}</th>
                <th style={thStyle}>{t('gsCompliant')}</th>
                <th style={thStyle}>{t('gsNote')}</th>
                <th style={thStyle}>{t('gsTaskStatusCol')}</th>
                {canEdit && <th style={thStyle}>{t('gsActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {result?.data?.map(row => {
                const memberStyle: React.CSSProperties = row.account_status === 'deactivated'
                  ? { color: 'var(--text-muted)', textDecoration: 'line-through' }
                  : {}

                const categoryName = row.category_display_name_en

                return (
                  <tr key={row.id} style={{ background: row.row_type === 'personal_task' ? 'var(--success-bg)' : undefined }}>
                    {/* Start date */}
                    <td style={cellStyle}>
                      {row.start_date ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>

                    {/* Complete date */}
                    <td style={cellStyle}>
                      {row.complete_date ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>

                    {/* Member */}
                    <td style={cellStyle}>
                      <span style={memberStyle}>
                        {row.member_display_name}
                        {row.is_group_lead && <span style={{ marginLeft: 3 }}>🎖</span>}
                      </span>
                    </td>

                    {/* Region + Group */}
                    <td style={cellStyle}>
                      <span style={{ fontSize: 11, background: 'var(--bg-elevated)', borderRadius: 3, padding: '1px 5px' }}>
                        {row.region_code_snapshot}
                      </span>
                      {' '}{row.group_name_snapshot}
                    </td>

                    {/* Category */}
                    <td style={cellStyle}>{categoryName}</td>

                    {/* Task */}
                    <td style={{ ...cellStyle, maxWidth: 200 }}>
                      <span title={row.counting_rule ?? undefined}>{row.task_name}</span>
                    </td>

                    {/* Volume */}
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {row.volume != null ? row.volume : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>

                    {/* Hours */}
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {parseFloat(row.hours).toFixed(2)}h
                    </td>

                    {/* Compliant */}
                    <td style={cellStyle}>
                      {row.is_compliant === null ? (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      ) : row.is_compliant ? (
                        <span style={{ color: 'var(--success)', fontSize: 12 }}>✓</span>
                      ) : (
                        <span style={{ color: 'var(--danger)', fontSize: 12, fontWeight: 500 }}>{t('dmNoncompliant')}</span>
                      )}
                    </td>

                    {/* Note */}
                    <td style={{ ...cellStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span title={row.note ?? undefined}>{row.note || <span style={{ color: 'var(--text-muted)' }}>—</span>}</span>
                    </td>

                    {/* Task Status */}
                    <td style={cellStyle}>
                      {row.task_status ? (
                        <span style={{
                          fontSize: 11,
                          padding: '1px 6px',
                          borderRadius: 3,
                          background: row.task_status === 'done' ? 'var(--success-bg)' : row.task_status === 'progress' ? 'var(--info-bg)' : 'var(--bg-page)',
                          color: row.task_status === 'done' ? 'var(--success)' : row.task_status === 'progress' ? 'var(--info)' : 'var(--text-muted)',
                          border: `1px solid ${row.task_status === 'done' ? 'var(--success)' : row.task_status === 'progress' ? 'var(--info)' : 'var(--border)'}`,
                        }}>
                          {t(row.task_status === 'todo' ? 'assignStatusTodo' : row.task_status === 'progress' ? 'assignStatusProgress' : 'assignStatusDone')}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>

                    {/* Actions */}
                    {canEdit && (
                      <td style={cellStyle}>
                        {row.work_log_id ? (
                          <button
                            onClick={() => navigate(`/data?work_log_id=${row.work_log_id}`)}
                            style={{
                              padding: '2px 10px',
                              background: 'var(--bg-surface)',
                              border: '1px solid var(--accent)',
                              color: 'var(--accent)',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            ✏ {t('gsEdit')}
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Totals bar */}
      {result && (
        <div style={{
          display: 'flex',
          gap: 24,
          alignItems: 'center',
          padding: '10px 16px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          marginTop: 8,
          fontSize: 13,
          fontWeight: 500,
        }}>
          <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{t('gsTotalsBar')}：</span>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>{t('gsTotalsTaskCount')}：</span>
            <strong>{result.totals.task_count}</strong>
          </span>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>{t('gsTotalsVolume')}：</span>
            <strong>{result.totals.volume_total}</strong>
          </span>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>{t('gsTotalsHours')}：</span>
            <strong>{result.totals.hours_total.toFixed(2)}h</strong>
          </span>
        </div>
      )}

      {/* Pagination */}
      {result && totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', marginTop: 14 }}>
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            style={{
              padding: '4px 12px',
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: page <= 1 ? 'var(--bg-page)' : 'var(--bg-surface)',
              cursor: page <= 1 ? 'not-allowed' : 'pointer',
              fontSize: 13,
            }}
          >
            ‹
          </button>
          <span style={{ lineHeight: '30px', fontSize: 13, color: 'var(--text-secondary)' }}>
            {page} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            style={{
              padding: '4px 12px',
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: page >= totalPages ? 'var(--bg-page)' : 'var(--bg-surface)',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              fontSize: 13,
            }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  )
}
